// Private contact details (phone, email, push token).
//
// They live in `contacts/{uid}`, readable only by the user and by people they
// have a booking or rental with (recorded in `connections/{a}/{b}`). Older app
// versions still read `users/{uid}`, so that copy is used as a fallback until
// the old copies are removed. Mirrors DataHandler.swift on iOS.

import { getDatabase, ref, get, update, runTransaction, push, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';

/** Another user's contact. Only call once a booking/rental exists between you. */
export async function fetchContact(uid) {
  const db = getDatabase();
  const userSnap = await get(ref(db, `users/${uid}`)).catch(() => null);
  const user = userSnap?.val() || {};
  let contact = {};
  try {
    contact = (await get(ref(db, `contacts/${uid}`))).val() || {};
  } catch {
    // No connection recorded (or rules not yet published) — fall back.
  }
  return {
    name:     user.name || user.username || '',
    username: user.username || '',
    number: contact.number || user.number || '',
    email:  contact.email  || user.email  || '',
  };
}

/**
 * Links two users through a booking ('bookings') or rental ('rentals') so each
 * can read the other's contact entry. Best-effort: a missing link only means
 * the lookup falls back, and the migration script re-adds links anyway.
 */
export function recordConnection(kind, id, a, b) {
  return update(ref(getDatabase()), {
    [`connections/${a}/${b}/${kind}/${id}`]: true,
    [`connections/${b}/${a}/${kind}/${id}`]: true,
  }).catch(() => {});
}

/** Saves the signed-in user's own phone/email to their private entry. */
export function updateOwnContact(fields) {
  const uid = getAuth().currentUser?.uid;
  if (!uid) return Promise.resolve();
  return update(ref(getDatabase(), `contacts/${uid}`), fields);
}

/**
 * Keeps the signed-in user's private entry complete on every sign-in: email
 * from Firebase Auth, and a phone number copied from the old `users` record
 * only if the private entry doesn't have one yet.
 */
export async function syncOwnContact() {
  const authUser = getAuth().currentUser;
  if (!authUser) return;
  const db = getDatabase();
  try {
    const contact = (await get(ref(db, `contacts/${authUser.uid}`))).val() || {};
    const legacyNumber = (await get(ref(db, `users/${authUser.uid}/number`))).val();
    const updates = {};
    if (authUser.email && contact.email !== authUser.email) updates.email = authUser.email;
    if (!contact.number && legacyNumber) updates.number = legacyNumber;
    if (Object.keys(updates).length) await update(ref(db, `contacts/${authUser.uid}`), updates);
  } catch {
    // Rules not yet published — nothing to sync into.
  }
}

/**
 * Books seats on a ride ('rideReserve') or offers seats on a request
 * ('rideRequest'). Seats are taken in a transaction so simultaneous bookings
 * can't oversell, then the booking is saved (seats are given back if that
 * fails), then the two users are linked. Resolves with the other user's
 * contact; rejects with a user-facing Error.
 */
export async function bookRide({ node, ride, seats, type }) {
  const db = getDatabase();
  const uid = getAuth().currentUser?.uid;
  if (!uid) throw new Error('You need to be signed in.');

  const seatsRef = ref(db, `${node}/${ride.id}/seats`);
  const result = await runTransaction(seatsRef, current => {
    if (current === null) return current; // not loaded yet — Firebase retries
    const available = parseInt(current, 10);
    if (isNaN(available) || seats > available) return; // abort
    return available - seats;
  });
  if (!result.committed) throw new Error('Not enough seats available.');

  const bookingRef = push(ref(db, 'bookings'));
  try {
    await set(bookingRef, {
      rideID: ride.id, bookerUserID: uid, providerUserID: ride.userID, type, seatsBooked: seats,
    });
  } catch (err) {
    await runTransaction(seatsRef, current => {
      const n = parseInt(current, 10);
      return isNaN(n) ? current : n + seats;
    }).catch(() => {});
    throw new Error("Couldn't save your booking: " + err.message);
  }

  await recordConnection('bookings', bookingRef.key, uid, ride.userID);
  return fetchContact(ride.userID);
}

/** "sms:" link for a phone number as people typed it. */
export function smsLink(number) {
  return `sms:${String(number || '').replace(/[^\d+]/g, '')}`;
}
